import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useNotif } from '@/context/NotificationContext';
import { useI18n } from '@/i18n';
import {
  MessageCircle,
  ChevronLeft,
  ExternalLink,
  Wifi,
  Crown,
  Users,
  UserPlus,
  Eye,
  Send,
  Plus,
  MoreVertical,
  Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import ChatComposer from '@/components/ChatComposer';
import UserLink from '@/components/UserLink';
import ImageLightbox from '@/components/ImageLightbox';

function Avatar({ u, size = 9 }) {
  const cls = `w-${size} h-${size} rounded-full bg-[#00F0FF] text-black grid place-items-center font-bold overflow-hidden text-xs shrink-0`;
  if (u?.avatar_url) return <img src={fileUrl(u.avatar_url)} alt={u.name || ''} className={`${cls} object-cover`} />;
  return <div className={cls}>{(u?.name || '?')[0]?.toUpperCase()}</div>;
}

function StatusDot({ online }) {
  return (
    <span
      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0A0A0A] ${
        online ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'
      }`}
      title={online ? 'En ligne' : 'Hors ligne'}
    />
  );
}

function ConvoyChip({ convoy }) {
  if (!convoy) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
      convoy.is_host ? 'bg-[#FFD479]/20 text-[#FFD479]' : 'bg-[#00F0FF]/20 text-[#00F0FF]'
    }`}>
      {convoy.is_host ? <Crown className="w-2.5 h-2.5" /> : <Users className="w-2.5 h-2.5" />}
      {convoy.is_host ? 'Hôte' : 'En convoi'}
    </span>
  );
}

function PersonRow({ person, myConvoy, onSendDm, onViewProfile, onJoinConvoy, onInviteToMine, onCreateConvoyAndInvite }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => { if (!wrapperRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid={`friends-row-${person.user_id}`}
        onClick={() => setMenuOpen((v) => !v)}
        className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition ${menuOpen ? 'bg-white/10' : 'hover:bg-white/5'}`}
      >
        <div className="relative">
          <Avatar u={person} size={10} />
          <StatusDot online={person.online} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold text-sm truncate">{person.name}</span>
            <ConvoyChip convoy={person.convoy} />
          </div>
          {person.convoy ? (
            <div className="text-[10px] text-white/50 truncate">
              {person.convoy.is_host ? 'Hôte de ' : 'Dans '} <span className="text-white/70 font-medium">{person.convoy.convoy_name}</span>
            </div>
          ) : (
            <div className="text-[10px] text-white/40">
              {person.online ? 'En ligne' : person.gamer_tag ? `@${person.gamer_tag}` : 'Hors ligne'}
            </div>
          )}
        </div>
        <MoreVertical className="w-4 h-4 text-white/30 shrink-0" />
      </button>

      {menuOpen && (
        <div className="absolute right-2 top-full mt-1 z-30 w-[230px] glass-strong border border-white/10 rounded-xl shadow-2xl py-1.5" data-testid={`friends-menu-${person.user_id}`}>
          {person.convoy && (
            <button
              type="button"
              data-testid={`friends-menu-join-${person.user_id}`}
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onJoinConvoy(person.convoy); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center gap-2 text-white"
            >
              <Send className="w-3.5 h-3.5 text-[#00F0FF]" /> Demander à rejoindre son convoi
            </button>
          )}
          {myConvoy?.is_host ? (
            <button
              type="button"
              data-testid={`friends-menu-invite-${person.user_id}`}
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onInviteToMine(person); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center gap-2 text-white"
            >
              <UserPlus className="w-3.5 h-3.5 text-[#FF007F]" /> Inviter dans mon convoi
            </button>
          ) : (
            <button
              type="button"
              data-testid={`friends-menu-create-invite-${person.user_id}`}
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onCreateConvoyAndInvite(person); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center gap-2 text-white"
            >
              <Plus className="w-3.5 h-3.5 text-[#FF007F]" /> Créer un convoi & inviter
            </button>
          )}
          <button
            type="button"
            data-testid={`friends-menu-dm-${person.user_id}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onSendDm(person); }}
            className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center gap-2 text-white"
          >
            <MessageCircle className="w-3.5 h-3.5 text-white/70" /> Envoyer un message
          </button>
          <button
            type="button"
            data-testid={`friends-menu-profile-${person.user_id}`}
            onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onViewProfile(person); }}
            className="w-full text-left px-3 py-2 text-xs hover:bg-white/10 inline-flex items-center gap-2 text-white"
          >
            <Eye className="w-3.5 h-3.5 text-white/70" /> Voir son profil
          </button>
        </div>
      )}
    </div>
  );
}

export default function ChatDrawer() {
  const { user } = useAuth();
  const { totalUnread, threads, openChatWith, setOpenChatWith, refresh } = useNotif();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [panel, setPanel] = useState({ friends: [], pending_in: [], following: [], my_convoy: null });
  const [loadingPanel, setLoadingPanel] = useState(false);
  const endRef = useRef(null);

  // Memoize unread threads filter — used twice in render (length check + map)
  const unreadThreads = useMemo(() => threads.filter((th) => th.unread > 0), [threads]);

  const loadMessages = async (u) => {
    if (!u) return;
    try {
      const { data } = await api.get(`/dm/${u.user_id}`);
      setMessages(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
    } catch { setMessages([]); }
  };

  const loadPanel = useCallback(async () => {
    if (!user || user === false) return;
    setLoadingPanel(true);
    try {
      const { data } = await api.get('/social/panel');
      setPanel(data || { friends: [], pending_in: [], following: [], my_convoy: null });
    } catch (_e) { /* silent */ }
    finally { setLoadingPanel(false); }
  }, [user]);

  // External trigger: setOpenChatWith from anywhere opens the drawer directly to that user
  useEffect(() => {
    if (openChatWith) {
      setActive(openChatWith);
      setOpen(true);
      setOpenChatWith(null);
    }
  }, [openChatWith, setOpenChatWith]);

  useEffect(() => { if (active && open) loadMessages(active); }, [active?.user_id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !open) return;
    const i = setInterval(() => loadMessages(active), 5000);
    return () => clearInterval(i);
  }, [active?.user_id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  // Refresh panel when drawer opens or every 20s while open
  useEffect(() => {
    if (!open || active) return undefined;
    loadPanel();
    const i = setInterval(loadPanel, 20000);
    return () => clearInterval(i);
  }, [open, active, loadPanel]);

  const hidden = location.pathname.startsWith('/messages') || !user || user === false;
  if (hidden) return null;

  const send = async ({ text, image_path }) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/dm/${active.user_id}`, { text, image_path });
      setMessages((prev) => [...prev, data]);
      refresh();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 50);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  // Actions
  const onSendDm = (p) => setActive({ user_id: p.user_id, name: p.name, avatar_url: p.avatar_url });
  const onViewProfile = (p) => { setOpen(false); navigate(`/u/${p.user_id}`); };
  const onJoinConvoy = async (convoy) => {
    try {
      const { data } = await api.post(`/convoys/${convoy.convoy_id}/join`);
      toast.success(data.pending_join ? "Demande envoyée à l'hôte" : 'Convoi rejoint');
      setOpen(false);
      navigate(`/convoys/${convoy.convoy_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const onInviteToMine = async (person) => {
    if (!panel.my_convoy?.is_host) return;
    try {
      await api.post(`/convoys/${panel.my_convoy.convoy_id}/invite/${person.user_id}`);
      toast.success(`${person.name} a été ajouté à ton convoi`);
      loadPanel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const onCreateConvoyAndInvite = async (person) => {
    const name = window.prompt('Nom de ton nouveau convoi ?', `Convoi de ${user.name || 'driver'}`);
    if (!name) return;
    try {
      const { data: convoy } = await api.post('/convoys', { name: name.trim(), max_size: 12, season: 'summer' });
      await api.post(`/convoys/${convoy.id}/invite/${person.user_id}`);
      toast.success(`Convoi créé · ${person.name} invité`);
      setOpen(false);
      navigate(`/convoys/${convoy.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const totalBadge = totalUnread;

  return (
    <>
      <button
        data-testid="chat-drawer-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#FF007F] hover:bg-[#FF007F]/90 text-white shadow-lg shadow-[#FF007F]/30 grid place-items-center transition-transform hover:scale-110"
        aria-label={t('chat.open')}
      >
        <Users className="w-6 h-6" />
        {totalBadge > 0 && (
          <span data-testid="chat-fab-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#00F0FF] text-black text-[11px] font-bold grid place-items-center border-2 border-[#0A0A0A]">
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="glass-strong border-white/10 text-white w-full sm:max-w-md p-0 overflow-hidden flex flex-col">
          <SheetHeader className="p-4 border-b border-white/10 flex-row items-center gap-2 space-y-0">
            {active && (
              <button data-testid="chat-back-btn" onClick={() => { setActive(null); setMessages([]); }} className="text-white/60 hover:text-white">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <SheetTitle className="font-display uppercase tracking-tight text-white flex-1">
              {active ? <UserLink userId={active.user_id} name={active.name} className="text-white" /> : (t('chat.friends_convoys') || 'Amis & convois')}
            </SheetTitle>
            <button data-testid="chat-open-full" onClick={() => { setOpen(false); navigate('/messages'); }}
              className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> {t('chat.quick.messages') || 'Messagerie'}
            </button>
          </SheetHeader>

          {!active ? (
            <div className="flex-1 overflow-y-auto p-3">
              {/* Quick actions: Messagerie + Tchat du convoi */}
              <div className="mb-4 grid grid-cols-2 gap-2" data-testid="chat-drawer-quick-actions">
                <button
                  type="button"
                  data-testid="chat-quick-messages"
                  onClick={() => { setOpen(false); navigate('/messages'); }}
                  className="relative flex items-center gap-2 p-2.5 rounded-xl border border-[#FF007F]/30 bg-[#FF007F]/10 hover:bg-[#FF007F]/20 transition text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#FF007F]/30 grid place-items-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-[#FF007F]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-white">{t('chat.quick.messages') || 'Messagerie'}</div>
                    <div className="text-[10px] text-white/50 truncate">{t('chat.quick.messages_sub') || 'Messages privés'}</div>
                  </div>
                  {totalUnread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#00F0FF] text-black text-[10px] font-bold grid place-items-center border-2 border-[#0A0A0A]">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  data-testid="chat-quick-convoy"
                  disabled={!panel.my_convoy}
                  onClick={() => {
                    if (!panel.my_convoy) { setOpen(false); navigate('/convoys'); return; }
                    setOpen(false);
                    navigate(`/convoys/${panel.my_convoy.convoy_id}#chat`);
                  }}
                  className="flex items-center gap-2 p-2.5 rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 disabled:opacity-40 disabled:cursor-not-allowed transition text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/30 grid place-items-center flex-shrink-0">
                    <Radio className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-white">{t('chat.quick.convoy') || 'Tchat du convoi'}</div>
                    <div className="text-[10px] text-white/50 truncate">
                      {panel.my_convoy ? panel.my_convoy.convoy_name : (t('chat.quick.convoy_none') || 'Aucun convoi actif')}
                    </div>
                  </div>
                </button>
              </div>

              {panel.my_convoy && (
                <div className="mb-4 p-3 rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] font-bold">{t('chat.my_convoy') || 'Mon convoi'}</div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate inline-flex items-center gap-1">
                        {panel.my_convoy.is_host && <Crown className="w-3 h-3 text-[#FFD479]" />}
                        {panel.my_convoy.convoy_name}
                      </div>
                      <div className="text-[10px] text-white/50">
                        {panel.my_convoy.members_count}/{panel.my_convoy.max_size} {t('chat.pilots') || 'pilotes'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); navigate(`/convoys/${panel.my_convoy.convoy_id}`); }}
                      className="text-[10px] uppercase tracking-wider font-bold text-[#00F0FF] hover:text-white"
                    >
                      {t('chat.open_short') || 'Ouvrir'} →
                    </button>
                  </div>
                </div>
              )}

              {/* Pending friend requests */}
              {panel.pending_in.length > 0 && (
                <section className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] font-bold mb-2 inline-flex items-center gap-1">
                    <UserPlus className="w-3 h-3" /> {t('chat.friend_requests') || "Demandes d'amis"} ({panel.pending_in.length})
                  </div>
                  <div className="space-y-1">
                    {panel.pending_in.map((p) => (
                      <PersonRow key={p.user_id} person={p} myConvoy={panel.my_convoy} onSendDm={onSendDm} onViewProfile={onViewProfile} onJoinConvoy={onJoinConvoy} onInviteToMine={onInviteToMine} onCreateConvoyAndInvite={onCreateConvoyAndInvite} />
                    ))}
                  </div>
                </section>
              )}

              {/* Friends */}
              <section className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-2 inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {t('chat.friends') || 'Amis'} ({panel.friends.length})
                </div>
                {panel.friends.length === 0 ? (
                  <div className="text-center text-white/40 text-xs py-4 italic">{t('chat.no_friends') || "Aucun ami pour l'instant."}</div>
                ) : (
                  <div className="space-y-1">
                    {panel.friends.map((p) => (
                      <PersonRow key={p.user_id} person={p} myConvoy={panel.my_convoy} onSendDm={onSendDm} onViewProfile={onViewProfile} onJoinConvoy={onJoinConvoy} onInviteToMine={onInviteToMine} onCreateConvoyAndInvite={onCreateConvoyAndInvite} />
                    ))}
                  </div>
                )}
              </section>

              {/* Following */}
              {panel.following.length > 0 && (
                <section className="mb-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-2 inline-flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {t('chat.following') || 'Joueurs suivis'} ({panel.following.length})
                  </div>
                  <div className="space-y-1">
                    {panel.following.map((p) => (
                      <PersonRow key={p.user_id} person={p} myConvoy={panel.my_convoy} onSendDm={onSendDm} onViewProfile={onViewProfile} onJoinConvoy={onJoinConvoy} onInviteToMine={onInviteToMine} onCreateConvoyAndInvite={onCreateConvoyAndInvite} />
                    ))}
                  </div>
                </section>
              )}

              {/* Unread DM threads (compact) */}
              {unreadThreads.length > 0 && (
                <section className="mt-6 pt-4 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-2 inline-flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> {t('chat.unread_messages') || 'Messages non lus'}
                  </div>
                  {unreadThreads.map((th) => (
                    <button
                      key={th.user_id}
                      data-testid={`chat-thread-${th.user_id}`}
                      onClick={() => setActive(th)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition"
                    >
                      <Avatar u={th} size={9} />
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-sm truncate">{th.name}</span>
                          <span className="text-[10px] bg-[#FF007F] rounded-full px-1.5 py-0.5 font-bold">{th.unread}</span>
                        </div>
                        <div className="text-xs text-white/50 truncate">{th.last_text}</div>
                      </div>
                    </button>
                  ))}
                </section>
              )}

              {loadingPanel && panel.friends.length === 0 && panel.pending_in.length === 0 && panel.following.length === 0 && (
                <div className="text-center text-white/40 text-xs py-8">Chargement…</div>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-drawer-messages">
                {messages.length === 0 && <div className="text-white/40 text-sm text-center py-8">{t('messages.empty_conv')}</div>}
                {messages.map((m) => {
                  const mine = m.from_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? 'bg-[#FF007F] text-white' : 'bg-white/10 text-white'}`}>
                        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                        {m.image_path && (
                          <button type="button" onClick={() => setLightbox(m.image_path)} className="block mt-1">
                            <img src={fileUrl(m.image_path)} alt="" className="max-h-48 rounded-lg hover:opacity-90 transition" />
                          </button>
                        )}
                        <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="border-t border-white/10 p-3">
                <ChatComposer
                  onSubmit={send}
                  placeholder={t('messages.placeholder')}
                  rows={1}
                  sendVariant="cyan"
                  testIdPrefix="chat-drawer"
                  sendOnEnter
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
